import React from 'react';
import {
  ConfigResponse,
  PartialPaymentValue,
  TippingPercentagesValue,
} from '../../store/modules/merchant/types';
import PageWrapper from '../../common/PageWrapper';
import { useMerchant } from '../../contexts/MerchantContext';
import { TippingForm } from './components/TippingForm';
import { toast } from '../../util/toast';
import { useGetConfigsQuery, useGetCredentialsQuery } from '../../store/modules/merchant/api';
import { useConfigUpsert } from '../../hooks';
import { CONFIG_SAVE_ERROR_FALLBACK } from '../../util/partialPaymentConfig';
import { getClientFacingApiErrorMessage } from '../../util/helpers';

export const Tipping = () => {
  const { merchantId, isLoading: merchantInfoLoading, canUpdateMerchantSettings } = useMerchant();
  const hasModifyAccess = canUpdateMerchantSettings();
  const { data: configs, isLoading: isLoadingConfigs } = useGetConfigsQuery(merchantId, {
    skip: !merchantId,
  });
  const tippingPercentagesConfig = configs?.find((c) => c.name === 'TIPPING_PERCENTAGES') as
    ConfigResponse<TippingPercentagesValue> | undefined;
  const partialPaymentsConfig = configs?.find((c) => c.name === 'PARTIAL_PAYMENTS') as
    ConfigResponse<PartialPaymentValue> | undefined;

  const { onConfigUpsert, isLoading: isSaving } = useConfigUpsert(merchantId);
  const { data: credentialData, isLoading: isLoadingCredentials } = useGetCredentialsQuery(
    { merchantId, type: ['ecommerce'] },
    { skip: !merchantId },
  );
  const ecommerceCredentials = credentialData?.ecommerce?.credentials ?? [];
  const isCustomMerchant = ecommerceCredentials.some(
    (credential) => credential?.systemOfOrigin === 'custom',
  );

  return (
    <PageWrapper
      showParentHierarchyBanner
      title="Tipping"
      description="When tipping is enabled, Skipify will display the option to tip to your customers during checkout."
      isLoading={merchantInfoLoading || isLoadingConfigs || isLoadingCredentials}
      data-testid="TIPPING"
    >
      <TippingForm
        key={tippingPercentagesConfig?.configId ?? merchantId}
        tippingPercentagesConfig={tippingPercentagesConfig}
        partialPaymentsConfig={partialPaymentsConfig}
        isTippingSupported={isCustomMerchant}
        onSave={async ({ value, enabled }) => {
          try {
            await onConfigUpsert({
              enabled,
              name: 'TIPPING_PERCENTAGES',
              value,
              configId: tippingPercentagesConfig?.configId,
              merchantId: tippingPercentagesConfig?.merchantId,
            });
            toast.success('Changes saved successfully');
          } catch (error) {
            toast.error(getClientFacingApiErrorMessage(error) || CONFIG_SAVE_ERROR_FALLBACK);
            throw error;
          }
        }}
        isSaving={isSaving}
        readOnly={!hasModifyAccess}
      />
    </PageWrapper>
  );
};
